import { useLayoutEffect, useState, type ReactNode } from "react";
import GridLayout, { getCompactor, useContainerWidth, type Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  GRID_COLS, GRID_MARGIN, GRID_ROWS, rowHeightFor, specFor,
  type Board, type SavedLayout,
} from "@shared/dashboardLayout";

export interface GridWidget {
  id: string;
  title: string;
  description: string;
  render: () => ReactNode;
}

// Vertical compaction without collision prevention: dragging a widget pushes
// its neighbours, which is how people expect to rearrange. A push that would
// run past GRID_ROWS is caught by validateLayout in useBoard and reverted.
const compactor = getCompactor("vertical", false, false);

/**
 * The one-screen grid for both dashboard tabs. Row height is computed from the
 * container's height, so the 20 rows always fill the space exactly and the page
 * never scrolls. Below lg the widgets stack in layout order instead.
 */
export function DashboardGrid({ board, layout, widgets, editing, onChange }: {
  board: Board;
  layout: SavedLayout;
  widgets: Record<string, GridWidget>;
  editing: boolean;
  onChange: (next: SavedLayout) => void;
}) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { width, containerRef, mounted } = useContainerWidth();
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setHeight(el.clientHeight);
    const ro = new ResizeObserver(([entry]) => setHeight(Math.floor(entry.contentRect.height)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, isDesktop]);

  const items = layout.items.filter((it) => widgets[it.id]);

  if (!isDesktop) {
    const ordered = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
    return (
      <div className="space-y-3">
        {ordered.map((it) => (
          <div key={it.id} style={{ height: `${Math.max(it.h, 4) * 2.5}rem` }}>{widgets[it.id].render()}</div>
        ))}
      </div>
    );
  }

  const rglLayout: Layout = items.map((it) => {
    const spec = specFor(board, it.id)!;
    return { i: it.id, x: it.x, y: it.y, w: it.w, h: it.h, minW: spec.size.minW, minH: spec.size.minH };
  });
  const commit = (next: Layout) =>
    onChange({ v: 1, items: next.map(({ i, x, y, w, h }) => ({ id: i, x, y, w, h })) });
  const remove = (id: string) => onChange({ v: 1, items: layout.items.filter((it) => it.id !== id) });

  return (
    <div ref={containerRef} className="flex-1 min-h-0 relative">
      {mounted && height > 0 && (
        <GridLayout
          width={width}
          layout={rglLayout}
          gridConfig={{ cols: GRID_COLS, rowHeight: rowHeightFor(height), margin: [GRID_MARGIN, GRID_MARGIN], containerPadding: [0, 0], maxRows: GRID_ROWS }}
          dragConfig={{ enabled: editing, cancel: "button,a,input,select,textarea" }}
          resizeConfig={{ enabled: editing, handles: ["s", "e", "se", "sw", "w"] }}
          compactor={compactor}
          autoSize={false}
          onDragStop={(l) => commit(l)}
          onResizeStop={(l) => commit(l)}
        >
          {rglLayout.map((it) => (
            <div
              key={it.i}
              className={cn("relative", editing && "rounded-lg outline-dashed outline-[1.5px] outline-offset-[-1px] outline-primary/50 cursor-move")}
            >
              {editing && (
                <button
                  type="button"
                  onClick={() => remove(it.i)}
                  title={`Remove ${widgets[it.i].title}`}
                  aria-label={`Remove ${widgets[it.i].title}`}
                  className="absolute -top-2 -right-2 z-20 w-6 h-6 rounded-full bg-card border border-border shadow-sm text-destructive grid place-items-center hover:bg-destructive hover:text-destructive-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <div className={cn("h-full", editing && "pointer-events-none select-none")}>{widgets[it.i].render()}</div>
            </div>
          ))}
        </GridLayout>
      )}
    </div>
  );
}
